import { computed } from "vue";
import type {
  AdminWalletAvailableAllocation,
  AdminWalletTransaction,
  WalletTransactionKind,
} from "@shopexperts/legacy-api";
import type { RouteLocationRaw } from "vue-router";
import { formatCurrency } from "@/v2Projects/utils/display";
import {
  getDirectionLabel,
  getTransactionKindMeta,
  getWalletEffectAmount,
  type WalletTransactionLike,
} from "@/components/expert/wallet/walletTransactionDisplay";
import {
  useWalletTransaction,
  type WalletTransactionReference,
} from "@/components/expert/wallet/useWalletTransaction";
import { useAdminExpertLevels } from "@/app/composables/expert-levels";
import { formatCommission } from "@/app/composables/expert-levels/expertLevelForm";
import { buildParties, expertProfileRoute } from "./transactionParty";

interface KeyedRow {
  key: string;
  label: string;
  value: string;
}

export interface SummaryStat {
  key: string;
  label: string;
  value: string;
  valueClass: string;
}

export interface AllocationSourceRow {
  key: string;
  sourceLabel: string;
  sourceTransactionId: number;
  holdTransactionId: number | null;
  amountLabel: string;
  /** "of $X" when the source's own amount is known; null otherwise. */
  sourceTotalLabel: string | null;
  /** true when this debit consumed only part of the source; null when unknown. */
  isPartialConsumption: boolean | null;
  partialTooltip: string | null;
  references: WalletTransactionReference[];
}

export interface BuildAllocationSourceRowsOptions {
  /** Phrase the partial tooltip for a debit that has not been posted yet. */
  prospective?: boolean;
  /** What the tooltip calls the consuming transaction, e.g. "debit" or "payout". */
  subject?: string;
}

export function buildAllocationSourceRows(
  allocations: AdminWalletAvailableAllocation[],
  money: (cents: number) => string,
  options: BuildAllocationSourceRowsOptions = {},
): AllocationSourceRow[] {
  const subject = options.subject ?? "debit";
  const prospective = options.prospective ?? false;
  return allocations.map((allocation) => {
    const projectRoute: RouteLocationRaw | null =
      allocation.project_id !== null
        ? { name: "admin-project-detail", params: { id: String(allocation.project_id) } }
        : null;

    const references: WalletTransactionReference[] = [];
    if (allocation.quote_id !== null) {
      references.push({
        key: "quote",
        label: `Quote #${allocation.quote_id}`,
        to: projectRoute
          ? {
              ...projectRoute,
              query: { tab: "quotes", quote: String(allocation.quote_id) },
              hash: `#quote-${allocation.quote_id}`,
            }
          : undefined,
      });
    }
    if (allocation.project_id !== null) {
      references.push({
        key: "project",
        label: `Project #${allocation.project_id}`,
        to: projectRoute ?? undefined,
      });
    }

    const kindLabel = allocation.source_transaction_kind
      ? getTransactionKindMeta(allocation.source_transaction_kind).label
      : "Transaction";

    const sourceTotalCents =
      typeof allocation.source_available_delta_cents === "number" &&
      allocation.source_available_delta_cents > 0
        ? allocation.source_available_delta_cents
        : null;
    const isPartialConsumption =
      sourceTotalCents !== null ? allocation.amount_cents < sourceTotalCents : null;
    const sourceRemainingCents = allocation.source_remaining_available_cents ?? null;

    let partialTooltip: string | null = null;
    if (isPartialConsumption && sourceTotalCents !== null) {
      partialTooltip = prospective
        ? `This ${subject} would consume ${money(allocation.amount_cents)} of the ${money(sourceTotalCents)} this source released`
        : `This ${subject} consumed ${money(allocation.amount_cents)} of the ${money(sourceTotalCents)} this source released`;
      if (sourceRemainingCents !== null && sourceRemainingCents > 0) {
        partialTooltip += prospective
          ? `; ${money(sourceRemainingCents)} would stay available.`
          : `; ${money(sourceRemainingCents)} is still available.`;
      } else if (sourceRemainingCents === 0) {
        partialTooltip += prospective ? "; the rest already funded other debits." : "; the rest funded other debits.";
      } else {
        partialTooltip += ".";
      }
    }

    return {
      key: String(allocation.source_transaction_id),
      sourceLabel: `${kindLabel} #${allocation.source_transaction_id}`,
      sourceTransactionId: allocation.source_transaction_id,
      holdTransactionId: allocation.hold_transaction_id,
      amountLabel: money(allocation.amount_cents),
      sourceTotalLabel:
        isPartialConsumption && sourceTotalCents !== null ? `of ${money(sourceTotalCents)}` : null,
      isPartialConsumption,
      partialTooltip,
      references,
    };
  });
}

interface BuildAdminWalletSummaryStatsOptions {
  money: (cents: number) => string;
  signedMoney: (cents: number) => string;
  optionalMoney: (cents: number | null) => string | null;
  projectNetCents: number | null;
}

export const ADMIN_TRANSACTION_DELETE_WINDOW_MS = 10 * 60 * 1000;

const ADMIN_DELETABLE_TRANSACTION_KINDS = new Set<WalletTransactionKind>([
  "admin_credit",
  "admin_debit",
  "admin_hold",
  "admin_external_payment",
  "admin_gross_sales_credit",
  "admin_gross_sales_debit",
  "admin_net_credit",
  "admin_net_debit",
]);

export function isAdminWalletTransactionKindDeletable(kind: WalletTransactionKind): boolean {
  return ADMIN_DELETABLE_TRANSACTION_KINDS.has(kind);
}

export function canDeleteAdminWalletTransaction(
  transaction: AdminWalletTransaction,
  options: {
    hasActiveFilters: boolean;
    latestLoadedTransactionId: number | undefined;
    nowMs: number;
  },
): boolean {
  if (options.hasActiveFilters || transaction.deleted_at || !transaction.created_at) return false;
  const createdAtMs = new Date(transaction.created_at).getTime();
  if (Number.isNaN(createdAtMs)) return false;

  return (
    isAdminWalletTransactionKindDeletable(transaction.transaction_kind) &&
    options.latestLoadedTransactionId === transaction.id &&
    options.nowMs >= createdAtMs &&
    options.nowMs - createdAtMs < ADMIN_TRANSACTION_DELETE_WINDOW_MS
  );
}

export function buildAdminWalletSummaryStats(
  transaction: AdminWalletTransaction,
  options: BuildAdminWalletSummaryStatsOptions,
): SummaryStat[] {
  const effect = getWalletEffectAmount(transaction);
  const walletEffectStats = (): SummaryStat[] => {
    const stats: SummaryStat[] = [
      {
        key: "available",
        label: transaction.remaining_hold_cents === null ? "Available" : "Released",
        value: options.signedMoney(effect.availableCents),
        valueClass:
          effect.availableCents > 0
            ? "font-semibold text-se-green"
            : "font-semibold text-se-dark",
      },
      {
        key: "hold",
        label: "On hold",
        value: effect.showSignedHold
          ? options.signedMoney(effect.holdCents)
          : options.money(effect.holdCents),
        valueClass: "font-semibold text-se-gray-700",
      },
    ];

    if (effect.refundedCents > 0) {
      stats.push({
        key: "refunded",
        label: "Refunded",
        value: `−${options.money(effect.refundedCents)}`,
        valueClass: "font-semibold text-se-gray-700",
      });
    }

    if (effect.debitedCents > 0) {
      stats.push({
        key: "debited",
        label: "Debited",
        value: `−${options.money(effect.debitedCents)}`,
        valueClass: "font-semibold text-se-danger",
      });
    }

    return stats;
  };

  if (transaction.transaction_kind === "admin_external_payment") {
    return [
      {
        key: "gross",
        label: "Gross",
        value: options.optionalMoney(transaction.gross_amount_cents) ?? "N/A",
        valueClass: "font-semibold text-se-dark",
      },
      {
        key: "available",
        label: "Available",
        value: options.signedMoney(effect.availableCents),
        valueClass:
          effect.availableCents > 0
            ? "font-semibold text-se-green"
            : "font-semibold text-se-dark",
      },
      {
        key: "hold",
        label: "On hold",
        value: options.money(effect.holdCents),
        valueClass: "font-semibold text-se-gray-700",
      },
    ];
  }

  if (effect.refundedCents > 0 || effect.debitedCents > 0) {
    return walletEffectStats();
  }

  const isNetAdjustment =
    transaction.transaction_kind === "admin_net_credit" ||
    transaction.transaction_kind === "admin_net_debit";
  const figures: (Omit<SummaryStat, "value"> & { value: string | null })[] = [
    {
      key: "gross",
      label: "Gross",
      value: options.optionalMoney(transaction.gross_amount_cents),
      valueClass: "font-semibold text-se-dark",
    },
    {
      key: "lifetime_net",
      label: "Net",
      value: isNetAdjustment ? options.optionalMoney(transaction.net_amount_cents) : null,
      valueClass: "font-semibold text-se-dark",
    },
    {
      key: "fee",
      label: "Proc. fee",
      value:
        transaction.provider_fee_cents === null
          ? null
          : `−${options.money(transaction.provider_fee_cents)}`,
      valueClass: "font-semibold text-se-gray-500",
    },
    {
      key: "net",
      label: "Net",
      value: options.optionalMoney(options.projectNetCents),
      valueClass: "font-semibold text-se-dark",
    },
    {
      key: "expert_share",
      label: "Expert share",
      value: isNetAdjustment ? null : options.optionalMoney(transaction.net_amount_cents),
      valueClass: "font-semibold text-se-green",
    },
    {
      key: "se_share",
      label: "SE share",
      value: options.optionalMoney(transaction.commission_cents),
      valueClass: "font-semibold text-se-dark",
    },
  ];

  const present = figures.filter(
    (figure): figure is SummaryStat => figure.value !== null,
  );
  return present.length ? present : walletEffectStats();
}

export function useAdminWalletTransaction(
  source: () => AdminWalletTransaction,
  options: { showOrganization: () => boolean },
) {
  const transaction = computed(() => source());

  const base = useWalletTransaction(() => source() as WalletTransactionLike, {
    perspective: "admin",
    resolveProjectRoute: (id): RouteLocationRaw => ({
      name: "admin-project-detail",
      params: { id: String(id) },
    }),
  });

  const { money, signedMoney, optionalMoney } = base;

  const hasLevelId = computed(() => transaction.value.expert_level_id !== null);
  const { levels } = useAdminExpertLevels({ enabled: hasLevelId });

  const appliedLevel = computed(() => {
    const id = transaction.value.expert_level_id;
    if (id === null) return null;
    return levels.value.find((level) => level.id === id) ?? null;
  });

  const hasLevelCommission = computed(
    () => hasLevelId.value || transaction.value.commission_cents !== null,
  );

  const appliedLevelTitle = computed(() => {
    if (appliedLevel.value) return appliedLevel.value.public_title;
    if (transaction.value.expert_level_id !== null) return `Level #${transaction.value.expert_level_id}`;
    return null;
  });

  const levelCommissionRateLabel = computed(() =>
    appliedLevel.value ? formatCommission(appliedLevel.value.commission_rate_bps) : null,
  );

  const commissionAmountLabel = computed(() => {
    if (transaction.value.commission_cents === null) return null;
    return formatCurrency(transaction.value.commission_cents, transaction.value.currency);
  });

  const internalNotesAdmin = computed(() => transaction.value.internal_notes_admin ?? null);

  const internalNotesAdminLabel = computed(() => {
    const admin = internalNotesAdmin.value;
    if (admin) return admin.name ?? admin.email ?? `Admin #${admin.id}`;

    const adminUserId = transaction.value.metadata?.admin_user_id;
    if (typeof adminUserId === "number" || typeof adminUserId === "string") {
      return `Admin #${adminUserId}`;
    }
    return null;
  });

  const isDeleted = computed(() => Boolean(transaction.value.deleted_at));

  const deletedAtLabel = computed(() => {
    if (!transaction.value.deleted_at) return null;
    const date = new Date(transaction.value.deleted_at);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  });

  const deletedByAdminLabel = computed(() => {
    const admin = transaction.value.deleted_by_admin ?? null;
    if (admin) return admin.name ?? admin.email ?? `Admin #${admin.id}`;
    if (transaction.value.deleted_by_admin_id) return `Admin #${transaction.value.deleted_by_admin_id}`;
    return null;
  });

  const deletedByAdmin = computed(() => transaction.value.deleted_by_admin ?? null);

  const deletedSummaryLabel = computed(() => {
    if (!isDeleted.value) return null;
    const parts = ["Deleted"];
    if (deletedByAdminLabel.value) parts.push(`by ${deletedByAdminLabel.value}`);
    if (deletedAtLabel.value) parts.push(`on ${deletedAtLabel.value}`);
    return parts.join(" ");
  });

  const timelineState = computed(() => {
    if (!isDeleted.value) return base.timelineState.value;
    return {
      id: "neutral" as const,
      label: "Deleted transaction",
      tone: "red" as const,
      icon: "delete" as const,
    };
  });

  const parties = computed(() => buildParties(transaction.value));
  const clientUser = computed(() => transaction.value.client ?? null);
  const expertUser = computed(() => transaction.value.expert ?? null);
  const expertRoute = computed<RouteLocationRaw | null>(() =>
    expertUser.value ? expertProfileRoute(expertUser.value.id) : null,
  );

  const kindChipLabel = computed(() =>
    transaction.value.transaction_kind === "admin_external_payment"
      ? "external payment"
      : transaction.value.transaction_kind.replace(/_/g, " "),
  );

  const externalPaymentState = computed(() => {
    if (transaction.value.transaction_kind !== "admin_external_payment") return null;
    const value = transaction.value.metadata?.external_payment;
    if (value === null || typeof value !== "object" || Array.isArray(value)) return null;

    const record = value as Record<string, unknown>;
    return {
      projectSettled:
        typeof record.project_settled === "boolean" ? record.project_settled : null,
      expertAlreadyPaid:
        typeof record.expert_already_paid === "boolean" ? record.expert_already_paid : null,
    };
  });

  const canReleaseHold = computed(() => !isDeleted.value && (transaction.value.remaining_hold_cents ?? 0) > 0);

  const allocationRows = computed<AllocationSourceRow[]>(() =>
    buildAllocationSourceRows(transaction.value.available_allocations ?? [], money),
  );

  const fundedRows = computed<AllocationSourceRow[]>(() => {
    return (transaction.value.funded_allocations ?? []).map((allocation) => {
      const kindLabel = allocation.debit_transaction_kind
        ? getTransactionKindMeta(allocation.debit_transaction_kind).label
        : "Transaction";

      const references: WalletTransactionReference[] = [];
      if (allocation.payout_request_id !== null) {
        references.push({
          key: "payout",
          label: `Payout request #${allocation.payout_request_id}`,
        });
      }

      return {
        key: String(allocation.debit_transaction_id),
        sourceLabel: `${kindLabel} #${allocation.debit_transaction_id}`,
        sourceTransactionId: allocation.debit_transaction_id,
        holdTransactionId: null,
        amountLabel: money(allocation.amount_cents),
        sourceTotalLabel: null,
        isPartialConsumption: null,
        partialTooltip: null,
        references,
      };
    });
  });

  const hasFundedDetails = computed(() => fundedRows.value.length > 0);

  const unallocatedAvailableCents = computed(
    () => transaction.value.unallocated_available_cents ?? 0,
  );

  const unallocatedAvailableLabel = computed(() =>
    unallocatedAvailableCents.value > 0 ? money(unallocatedAvailableCents.value) : null,
  );

  const hasAllocationDetails = computed(
    () =>
      transaction.value.available_allocations !== undefined &&
      (allocationRows.value.length > 0 || unallocatedAvailableCents.value > 0),
  );

  const canRequestDelete = computed(() => !isDeleted.value);

  const updatedLabel = computed(() => {
    if (!transaction.value.updated_at) return null;
    const date = new Date(transaction.value.updated_at);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  });

  const adminDetails = computed<KeyedRow[]>(() => {
    const t = transaction.value;
    const externalState = externalPaymentState.value;
    const rows: KeyedRow[] = [];
    if (options.showOrganization()) {
      rows.push({ key: "org", label: "Organization", value: `#${t.organization_id}` });
    }
    rows.push({ key: "wallet", label: "Wallet", value: `#${t.wallet_id}` });
    rows.push({
      key: "kind",
      label: "Kind / direction",
      value: `${kindChipLabel.value} · ${getDirectionLabel(t.direction)}`,
    });
    if (t.related_transaction_id !== null) {
      rows.push({ key: "related", label: "Related transaction", value: `#${t.related_transaction_id}` });
    }
    if (typeof t.allocated_available_cents === "number") {
      rows.push({
        key: "allocated-available",
        label: "Consumed by debits",
        value: money(t.allocated_available_cents),
      });
    }
    if (typeof t.remaining_available_cents === "number") {
      rows.push({
        key: "remaining-available",
        label: "Remaining available",
        value: money(t.remaining_available_cents),
      });
    }
    if (t.quote_payment_event_id !== null) {
      rows.push({ key: "qpe", label: "Quote payment event", value: `#${t.quote_payment_event_id}` });
    }
    if (t.payment_provider) {
      rows.push({ key: "provider", label: "Payment provider", value: t.payment_provider });
    }
    if (typeof externalState?.projectSettled === "boolean") {
      rows.push({
        key: "project-settled",
        label: "Project settled",
        value: externalState.projectSettled ? "Yes" : "No",
      });
    }
    if (typeof externalState?.expertAlreadyPaid === "boolean") {
      rows.push({
        key: "expert-already-paid",
        label: "Expert already paid",
        value: externalState.expertAlreadyPaid ? "Yes" : "No",
      });
    }
    if (updatedLabel.value) {
      rows.push({ key: "updated", label: "Last updated", value: updatedLabel.value });
    }
    if (deletedSummaryLabel.value) {
      rows.push({ key: "deleted", label: "Deleted", value: deletedSummaryLabel.value });
    }
    return rows;
  });

  const copyableIdentifiers = computed<KeyedRow[]>(() => {
    const t = transaction.value;
    const rows: KeyedRow[] = [];
    if (t.provider_payment_intent_id) {
      rows.push({ key: "intent", label: "Provider intent", value: t.provider_payment_intent_id });
    }
    if (t.provider_payment_link_id) {
      rows.push({ key: "link", label: "Provider payment link", value: t.provider_payment_link_id });
    }
    return rows;
  });

  const metadataEntries = computed<{ key: string; value: string }[]>(() => {
    const meta = transaction.value.metadata;
    if (!meta || typeof meta !== "object") return [];
    return Object.entries(meta).map(([key, value]) => ({
      key,
      value: typeof value === "string" ? value : JSON.stringify(value),
    }));
  });

  const projectNetCents = computed<number | null>(() => {
    const t = transaction.value;
    if (
      t.transaction_kind === "admin_external_payment" ||
      t.transaction_kind === "admin_gross_sales_credit" ||
      t.transaction_kind === "admin_gross_sales_debit"
    ) {
      return null;
    }
    if (t.gross_amount_cents !== null) {
      return t.gross_amount_cents - (t.provider_fee_cents ?? 0);
    }
    if (t.commission_cents !== null && t.net_amount_cents !== null) {
      return t.commission_cents + t.net_amount_cents;
    }
    return null;
  });

  const summaryStats = computed<SummaryStat[]>(() => {
    return buildAdminWalletSummaryStats(transaction.value, {
      money,
      signedMoney,
      optionalMoney,
      projectNetCents: projectNetCents.value,
    });
  });

  return {
    ...base,
    transaction,
    timelineState,
    hasLevelCommission,
    appliedLevelTitle,
    levelCommissionRateLabel,
    commissionAmountLabel,
    internalNotesAdmin,
    internalNotesAdminLabel,
    parties,
    clientUser,
    expertUser,
    expertRoute,
    kindChipLabel,
    isDeleted,
    deletedAtLabel,
    deletedByAdmin,
    deletedByAdminLabel,
    deletedSummaryLabel,
    canReleaseHold,
    canRequestDelete,
    allocationRows,
    unallocatedAvailableLabel,
    hasAllocationDetails,
    fundedRows,
    hasFundedDetails,
    adminDetails,
    copyableIdentifiers,
    metadataEntries,
    summaryStats,
  };
}
